/** @jsx h */
import { h } from "hyperapp";
import nestable from "hyperapp-nestable";
import classNames from "classnames";
import bus from "../../../bus";
import { playerEvents } from "../../events";
import { Fragment, WrappedSvg } from "../../../utilities/hyperapp";

import PlayerControls from "./controls";
import PlayerTime from "./time";
import PlayerShare from "./share";

import playIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player/play-icon.svg";
import pauseIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player/pause-icon.svg";
import playerBarsIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player-bars/bars.svg";
import noDurationLine from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player/no-duration-line.svg";

const initialState = {
  currentItemPlayerItemID: null,
  currentItemTitle: null,
  currentItemSubtitle: null,
  currentItemLive: false
};

const initialActions = {
  init: props => {
    const {
      set,
      activate,
      advertisement,
      availableStreams,
      currentItemPlayerItemID,
      currentTargetType,
      currentItemTitle,
      currentItemSubtitle,
      currentItemLive,
      currentItemReady,
      isOpen,
      target,
      status
    } = props;
    return {
      set,
      activate,
      advertisement,
      availableStreams,
      currentItemPlayerItemID,
      currentTargetType,
      currentItemTitle,
      currentItemSubtitle,
      currentItemLive,
      currentItemReady,
      isOpen,
      status,
      target,
      tick: Date.now()
    };
  },
  onTick: () => {
    return {
      tick: Date.now()
    };
  },
  onSeekTo: time => ({ target }) => {
    if (target.currentItem().hasDuration()) {
      target.currentItem().seekTo(time);
    }
  },
  onSet: playerState => ({ set, activate }) => {
    set(playerState);
    activate();
  },
  onStop: () => (state, actions) => {
    actions.setStatus("stopped");
  },
  onPlay: () => (state, actions) => {
    actions.setStatus("playing");
  },
  onPause: () => (state, actions) => {
    actions.setStatus("paused");
  },
  onRequestStatus: () => (state, actions) => {
    const {
      currentItemPlayerItemID,
      currentTargetType,
      currentItemTitle,
      currentItemSubtitle,
      currentItemLive,
      currentItemReady,
      status
    } = state;
    bus.emit(playerEvents.STATUS_CHANGE, {
      currentItemPlayerItemID,
      currentTargetType,
      currentItemTitle,
      currentItemSubtitle,
      currentItemLive,
      currentItemReady,
      status
    });
  },
  setStatus: status => ({ set, activate }) => {
    if (status === "playing") {
      activate();
    }
    set({ status });
    return { status };
  },
  setDefaultStream: () => (state, actions) => {
    if (state.availableStreams && state.availableStreams.length > 0) {
      state.set({
        target: {
          type: "stream",
          id: state.availableStreams[0].id
        }
      });
    }
  },
  onUpdate: newProps => state => {
    // TODO: Refact all the `currentItem` into a single object
    let {
      target,
      availableStreams,
      currentItemPlayerItemID,
      currentTargetType,
      currentItemTitle,
      currentItemSubtitle,
      currentItemLive,
      currentItemReady,
      isOpen,
      status
    } = newProps;
    // FIXME Manually override isReady
    currentItemReady = target.currentItem()
      ? target.currentItem().isReady()
      : currentItemReady;
    // State trails props here
    const changed =
      state.availableStreams !== availableStreams ||
      state.status !== status ||
      state.currentItemPlayerItemID !== currentItemPlayerItemID ||
      state.currentItemTitle !== currentItemTitle ||
      state.currentItemSubtitle !== currentItemSubtitle ||
      state.currentItemLive !== currentItemLive ||
      state.currentItemReady !== currentItemReady;
    const newState = {
      availableStreams,
      currentItemPlayerItemID,
      currentTargetType,
      currentItemTitle,
      currentItemSubtitle,
      currentItemLive,
      currentItemReady,
      isOpen,
      status,
      target
    };
    bus.emit(playerEvents.STATUS_CHANGE, newState);
    if (changed) {
      return newState;
    }
  },
  onCreate: () => (state, actions) => {
    bus.on(playerEvents.STOP, actions.onStop);
    bus.on(playerEvents.PLAY, actions.onPlay);
    bus.on(playerEvents.PAUSE, actions.onPause);
    bus.on(playerEvents.SET, actions.onSet);
    bus.on(playerEvents.SEEK_TO, actions.onSeekTo);
    bus.on(playerEvents.REQUEST_STATUS, actions.onRequestStatus);
    bus.on(playerEvents.PLAY_DEFAULT_STREAM, actions.setDefaultStream);
    bus.on(playerEvents.TICK, actions.onTick);
  },
  onDestroy: () => (state, actions) => {
    bus.removeListener(playerEvents.STOP, actions.onStop);
    bus.removeListener(playerEvents.PLAY, actions.onPlay);
    bus.removeListener(playerEvents.PAUSE, actions.onPause);
    bus.removeListener(playerEvents.SEEK_TO, actions.onSeekTo);
    bus.removeListener(playerEvents.SET, actions.onSet);
    bus.removeListener(playerEvents.REQUEST_STATUS, actions.onRequestStatus);
    bus.removeListener(playerEvents.TICK, actions.onTick);
  }
};

const Player = nestable(
  initialState,
  initialActions,
  (state, actions) => props => {
    const { status, muted, volume, quality, target, set } = props;

    if (!target || !target.currentItem()) {
      return null;
    }

    return (
      <div
        oncreate={actions.onCreate}
        ondestroy={actions.onDestroy}
        onupdate={() => {
          actions.onUpdate(props);
          // Render current item
          target.currentItem().abstractRender();
        }}
      >
        <PlayerControls
          target={target}
          muted={muted}
          volume={volume}
          quality={quality}
          set={set}
        />
        <button
          class={classNames(
            "player",
            "player--sidebar",
            "d-flex",
            "flex-items-v-center",
            {
              "player--is-loading": !target.currentItem().isReady(),
              "player--is-playing": status === "playing"
            }
          )}
          onclick={e => {
            set({ status: status === "playing" ? "paused" : "playing" });
          }}
          ga-on="click,auxclick,contextmenu"
          ga-event-category="audio"
          ga-event-action={status === "playing" ? "pause" : "play"}
          ga-event-label={target.currentItem().getTitle()}
        >
          <div class="player__play-button-wrapper">
            <div class="player__play-button d-flex flex-items-center">
              <span class="player__loading-icon" />
              <span class="player__play-icon">
                <WrappedSvg data={playIcon} />
              </span>
              <span class="player__pause-icon">
                <WrappedSvg data={pauseIcon} />
              </span>
            </div>
          </div>
          <div class="player__details">
            {target.currentItem().isLive() ? (
              <span class="player__on-air caps-heading">
                <span class="player-bars">
                  <WrappedSvg data={playerBarsIcon} />
                </span>{" "}
                On Air
              </span>
            ) : null}
            <span class="player__title">{target.currentItem().getTitle()}</span>
            <span class="player__subtitle">
              {target.currentItem().getSubtitle()}
            </span>
          </div>
        </button>
        {target.currentItem().hasDuration() ? (
          <Fragment>
            <PlayerTime
              seekTo={target.currentItem().seekTo}
              getCurrentTime={target.currentItem().getCurrentTime}
              duration={target.currentItem().getDuration()}
            />
            <PlayerShare
              key={state.currentItemPlayerItemID}
              label={target.currentItem().getLabel()}
              duration={target.currentItem().getDuration()}
              title={target.currentItem().getTitle()}
              currentTime={target.currentItem().getCurrentTime()}
              getCurrentTime={target.currentItem().getCurrentTime}
              shareURL={target.currentItem().getCanonicalPath()}
              sharedMomentBaseURL={target
                .currentItem()
                .getSharedMomentBaseURL()}
            />
          </Fragment>
        ) : (
          <div class="player__no-duration d-flex flex-items-v-center mt-small mb-medium">
            <WrappedSvg data={noDurationLine} />
          </div>
        )}
      </div>
    );
  },
  "player-"
);

export default Player;
